import { useCallback, useEffect, useMemo, useState } from "react";
import {
  EuiBadge,
  EuiBasicTableColumn,
  EuiButton,
  EuiCallOut,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiInMemoryTable,
  EuiLink,
  EuiLoadingSpinner,
  EuiPage,
  EuiPageBody,
  EuiPanel,
  EuiSpacer,
  EuiStat,
  EuiText,
  EuiTitle,
  EuiToolTip,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import {
  OPSANIO_VERSION_REF_KINDS,
  OpsanioLatestVersionsResponse,
  OpsanioModuleVersions,
  OpsanioVersionRef,
  OpsanioVersionRefKind,
} from "src/questionnaires";
import { isPipelineBad, PipelineBadge } from "./pipeline";

export * from "./pipeline";

const I18N = "anketa.opsanio.version";

/**
 * Mirrors `DEFAULT_ACTIVITY_WINDOW_DAYS` on the server. Only used before the
 * first response lands - every message that quotes a window prefers the value
 * the backend actually filtered on, so the two can never silently disagree.
 */
const FALLBACK_WINDOW_DAYS = 30;

/**
 * Column headers. The semver channels are labelled by their *shape* rather than
 * a fixed name, because the concrete label differs per project (`v1.12` vs
 * `v1.0`) and is rendered inside the cell.
 */
const CHANNEL_HEADING: Record<OpsanioVersionRefKind, string> = {
  dev: "dev",
  main: "main",
  default: "default branch",
  major: "v.X",
  minor: "v.X.Y",
  patch: "v.X.Y.Z",
};

/** Health of a project's *release* channels - dev is excluded on purpose. */
type ReleaseHealth = "failing" | "ok" | "none";

/** A table row: the payload plus the derived fields the table sorts/filters on. */
interface VersionRow extends OpsanioModuleVersions {
  releaseHealth: ReleaseHealth;
  /**
   * How many of this project's populated channels have a bad pipeline. This is
   * the default sort key (descending), so whatever is most broken is on top.
   */
  failedCount: number;
  /** Channels that carry a pipeline at all - the denominator for the badge. */
  channelCount: number;
  /** "Ja"/"Nein" rather than a boolean so the filter reads properly. */
  tracked: string;
}

const findRef = (
  row: OpsanioModuleVersions,
  kind: OpsanioVersionRefKind,
): OpsanioVersionRef | undefined => row.refs.find((r) => r.kind === kind);

const releaseHealthOf = (row: OpsanioModuleVersions): ReleaseHealth => {
  const release = row.refs.filter((r) => r.kind !== "dev" && r.ref);
  if (release.some((r) => isPipelineBad(r.pipeline?.status))) return "failing";
  return release.length ? "ok" : "none";
};

/**
 * Failing channels across the whole project, `dev` included - the ranking is
 * meant to answer "what is most broken", and a broken dev branch counts. The
 * narrower release-only view stays available through the health filter.
 */
const failedCountOf = (row: OpsanioModuleVersions): number =>
  row.refs.filter((r) => r.ref && isPipelineBad(r.pipeline?.status)).length;

const channelCountOf = (row: OpsanioModuleVersions): number =>
  row.refs.filter((r) => r.ref && r.pipeline).length;

/**
 * `opsanio > Versionen` - the release-side counterpart of the wave update
 * manager: for every active project in the opsanio GitLab group, what is
 * currently on each release channel and whether its CI pipeline passed.
 *
 * Read-only. Gated on `opsanio/updates · view`, both in the route table and
 * again on `GET /api/v2/opsanio/gitlab/versions`, which is the actual boundary.
 */
export const OpsanioVersionOverview = (): JSX.Element => {
  const { translate } = useAnketaI18n();
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();

  const [payload, setPayload] = useState<OpsanioLatestVersionsResponse | null>(
    null,
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const canFetch = typeof keycloak?.token === "string";

  const load = useCallback(
    async (force = false): Promise<void> => {
      if (!canFetch) return;
      setLoading(true);
      setError(null);

      // useApi never throws on an HTTP error - it reports through
      // `success`/`error`, so branch on those rather than using try/catch.
      const { success, data, error: err } =
        await Get<OpsanioLatestVersionsResponse>({
          path:
            `v2/${AnketaResourcesEnum.opsanio}/gitlab/versions` +
            (force ? "?refresh=1" : ""),
          responseType: "json",
          token: keycloak.token,
        });

      if (!success || !data) {
        setError(
          err?.message ??
            translate(`${I18N}.error.unknown`, {
              defaultMessage: "Die Versionen konnten nicht geladen werden.",
            }),
        );
        setPayload(null);
        setLoading(false);
        return;
      }

      setPayload(data);
      setLoading(false);
    },
    // `Get` and `translate` are intentionally omitted: useApi() and the i18n
    // context hand out fresh closures on every render, so listing them here
    // would re-create `load` each render and turn the effect below into a
    // refetch loop. Neither closes over state this callback needs to see.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canFetch, keycloak?.token],
  );

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo<VersionRow[]>(
    () =>
      (payload?.modules ?? [])
        .map((m) => ({
          ...m,
          releaseHealth: releaseHealthOf(m),
          failedCount: failedCountOf(m),
          channelCount: channelCountOf(m),
          tracked: m.waveTracked
            ? translate(`${I18N}.tracked.yes`, { defaultMessage: "Wave-Modul" })
            : translate(`${I18N}.tracked.no`, { defaultMessage: "Sonstiges" }),
        }))
        // Pre-sorted by path so that ties in the default `failedCount` sort fall
        // out alphabetically - Array.prototype.sort is stable, and EUI sorts
        // this array rather than a copy of the original order.
        .sort((a, b) => a.projectPath.localeCompare(b.projectPath)),
    // `translate` omitted for the same reason as above; a language switch
    // re-renders the page anyway, which recomputes these labels.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payload],
  );

  const failing = useMemo(
    () => rows.filter((r) => r.releaseHealth === "failing"),
    [rows],
  );
  const unreadable = useMemo(() => rows.filter((r) => r.error), [rows]);

  /** Groups present in the payload, for the group filter. */
  const groups = useMemo(
    () => Array.from(new Set(rows.map((r) => r.group))).sort(),
    [rows],
  );

  /**
   * The `default` column only earns its width when some project actually has an
   * odd default branch - it is empty for the ~95% that live on dev or main.
   */
  const showDefaultColumn = useMemo(
    () => rows.some((r) => findRef(r, "default")?.ref),
    [rows],
  );

  const renderChannelCell = (
    row: OpsanioModuleVersions,
    kind: OpsanioVersionRefKind,
  ): JSX.Element => {
    const ref = findRef(row, kind);

    // Gate on `ref`, not `label`: a missing `dev` branch still carries the
    // label "dev" so the backend can say *which* ref was looked for, and
    // rendering that would imply a branch that does not exist.
    if (!ref || !ref.ref) {
      return (
        <EuiText size="xs" color="subdued">
          -
        </EuiText>
      );
    }

    // A channel whose label is not itself a git ref (no bare `vX` tag exists in
    // the opsanio repos) shows what it actually resolved to, so the pipeline
    // badge below it is never attributed to the wrong commit.
    const resolved = ref.derived ? (
      <EuiText size="xs" color="subdued">
        <EuiIcon type="sortRight" size="s" /> {ref.ref}
      </EuiText>
    ) : null;

    return (
      <EuiFlexGroup direction="column" gutterSize="xs" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiText size="s">
            <EuiIcon type={ref.type === "branch" ? "branch" : "tag"} size="s" />{" "}
            {row.webUrl ? (
              <EuiLink
                href={`${row.webUrl}/-/tree/${encodeURIComponent(ref.ref)}`}
                target="_blank"
              >
                <strong>{ref.label}</strong>
              </EuiLink>
            ) : (
              <strong>{ref.label}</strong>
            )}
          </EuiText>
        </EuiFlexItem>
        {resolved && <EuiFlexItem grow={false}>{resolved}</EuiFlexItem>}
        <EuiFlexItem grow={false}>
          <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
            <EuiFlexItem grow={false}>
              <PipelineBadge pipeline={ref.pipeline ?? null} />
            </EuiFlexItem>
            {ref.commit && (
              <EuiFlexItem grow={false}>
                <EuiToolTip
                  content={`${ref.commit.title} - ${
                    ref.commit.author_name
                  }, ${new Date(ref.commit.committed_date).toLocaleString()}`}
                >
                  <EuiText size="xs" color="subdued">
                    {ref.commit.short_id}
                  </EuiText>
                </EuiToolTip>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </EuiFlexItem>
      </EuiFlexGroup>
    );
  };

  const channelColumns = OPSANIO_VERSION_REF_KINDS.filter(
    (kind) => kind !== "default" || showDefaultColumn,
  );

  const columns: Array<EuiBasicTableColumn<VersionRow>> = [
    {
      field: "module",
      name: translate(`${I18N}.column.module`, { defaultMessage: "Projekt" }),
      sortable: true,
      truncateText: true,
      render: (module: string, row: VersionRow) => (
        <>
          <EuiText size="s">
            {row.webUrl ? (
              <EuiLink href={row.webUrl} target="_blank" external>
                <strong>{module}</strong>
              </EuiLink>
            ) : (
              <strong>{module}</strong>
            )}
          </EuiText>
          <EuiFlexGroup gutterSize="xs" responsive={false} wrap>
            {row.waveTracked && (
              <EuiFlexItem grow={false}>
                <EuiToolTip
                  content={translate(`${I18N}.tracked.tooltip`, {
                    defaultMessage:
                      "Dieses Projekt liefert eine Versionsangabe für den Wave-Update-Manager.",
                  })}
                >
                  <EuiBadge color="accent">
                    {translate(`${I18N}.tracked.yes`, {
                      defaultMessage: "Wave-Modul",
                    })}
                  </EuiBadge>
                </EuiToolTip>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
          {row.error && (
            <EuiBadge color="danger" iconType="alert">
              {row.error}
            </EuiBadge>
          )}
        </>
      ),
    },
    {
      field: "failedCount",
      name: translate(`${I18N}.column.failed`, { defaultMessage: "Fehler" }),
      sortable: true,
      width: "90px",
      align: "center",
      render: (failedCount: number, row: VersionRow) => (
        <EuiToolTip
          content={
            failedCount
              ? translate(`${I18N}.failed.tooltip`, {
                  defaultMessage:
                    "{{failed}} von {{total}} Kanälen mit Pipeline sind fehlgeschlagen oder abgebrochen.",
                  values: { failed: failedCount, total: row.channelCount },
                })
              : translate(`${I18N}.failed.tooltip.none`, {
                  defaultMessage: "Keine fehlgeschlagene Pipeline.",
                })
          }
        >
          <EuiBadge color={failedCount ? "danger" : "success"}>
            {failedCount
              ? `${failedCount}/${row.channelCount}`
              : String(failedCount)}
          </EuiBadge>
        </EuiToolTip>
      ),
    },
    {
      field: "group",
      name: translate(`${I18N}.column.group`, { defaultMessage: "Gruppe" }),
      sortable: true,
      width: "180px",
      render: (group: string) => (
        <EuiText size="xs" color="subdued">
          {group}
        </EuiText>
      ),
    },
    ...channelColumns.map(
      (kind): EuiBasicTableColumn<VersionRow> => ({
        name: CHANNEL_HEADING[kind],
        // No `field`: the cell is derived from `refs`, so render off the row.
        render: (row: VersionRow) => renderChannelCell(row, kind),
      }),
    ),
    {
      field: "lastActivityAt",
      name: translate(`${I18N}.column.activity`, {
        defaultMessage: "Letzte Aktivität",
      }),
      sortable: true,
      width: "120px",
      render: (value: string) => (
        <EuiText size="xs" color="subdued">
          {value ? new Date(value).toLocaleDateString() : "-"}
        </EuiText>
      ),
    },
  ];

  if (loading && !payload) {
    return (
      <EuiPage paddingSize="l">
        <EuiPageBody>
          <EuiEmptyPrompt
            icon={<EuiLoadingSpinner size="xl" />}
            title={
              <h2>
                {translate(`${I18N}.loading`, {
                  defaultMessage: "Lade Versionen...",
                })}
              </h2>
            }
            body={
              <p>
                {translate(`${I18N}.loading.body`, {
                  defaultMessage:
                    "Projekte der GitLab-Gruppe opsanio mit Aktivität in den letzten {{days}} Tagen werden abgefragt - inaktive Projekte sind nicht enthalten. Der erste Aufruf dauert einige Sekunden.",
                  values: { days: payload?.windowDays ?? FALLBACK_WINDOW_DAYS },
                })}
              </p>
            }
          />
        </EuiPageBody>
      </EuiPage>
    );
  }

  return (
    <EuiPage paddingSize="l">
      <EuiPageBody>
        <EuiFlexGroup
          justifyContent="spaceBetween"
          alignItems="flexStart"
          gutterSize="m"
        >
          <EuiFlexItem grow>
            <EuiFlexGroup gutterSize="m" alignItems="center" responsive={false}>
              <EuiFlexItem grow={false}>
                <EuiIcon type="tag" size="xl" color="primary" />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiTitle size="l">
                  <h1>
                    {translate(`${I18N}.title`, {
                      defaultMessage: "Opsanio Versionen",
                    })}
                  </h1>
                </EuiTitle>
                <EuiText size="s" color="subdued">
                  {translate(`${I18N}.subtitle`, {
                    defaultMessage:
                      "Aktuelle Version je Projekt und Kanal - dev, main und die drei Semver-Stufen - inklusive Status der zugehörigen Pipeline. Alle Projekte der GitLab-Gruppe opsanio mit Aktivität in den letzten {{days}} Tagen.",
                    values: {
                      days: payload?.windowDays ?? FALLBACK_WINDOW_DAYS,
                    },
                  })}
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiToolTip
              content={translate(`${I18N}.refresh.tooltip`, {
                defaultMessage:
                  "Umgeht den 5-Minuten-Cache des Servers und fragt GitLab neu ab.",
              })}
            >
              <EuiButton
                iconType="refresh"
                onClick={() => load(true)}
                isLoading={loading}
              >
                {translate(`${I18N}.refresh`, {
                  defaultMessage: "Aktualisieren",
                })}
              </EuiButton>
            </EuiToolTip>
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiSpacer size="l" />

        {error && (
          <>
            <EuiCallOut
              title={translate(`${I18N}.error.title`, {
                defaultMessage: "Fehler beim Laden",
              })}
              color="danger"
              iconType="alert"
            >
              <p>{error}</p>
            </EuiCallOut>
            <EuiSpacer />
          </>
        )}

        <EuiFlexGroup gutterSize="m">
          <EuiFlexItem>
            <EuiPanel hasBorder paddingSize="m">
              <EuiStat
                title={String(rows.length)}
                description={translate(`${I18N}.stat.projects`, {
                  defaultMessage: "Projekte (letzte {{days}} Tage)",
                  values: {
                    days: payload?.windowDays ?? FALLBACK_WINDOW_DAYS,
                  },
                })}
                titleSize="m"
              />
            </EuiPanel>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiPanel hasBorder paddingSize="m">
              <EuiStat
                title={String(groups.length)}
                description={translate(`${I18N}.stat.groups`, {
                  defaultMessage: "Gruppen",
                })}
                titleSize="m"
              />
            </EuiPanel>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiPanel hasBorder paddingSize="m">
              <EuiStat
                title={String(failing.length)}
                description={translate(`${I18N}.stat.failing`, {
                  defaultMessage: "mit fehlgeschlagener Release-Pipeline",
                })}
                titleSize="m"
                titleColor={failing.length ? "danger" : "success"}
              />
            </EuiPanel>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiPanel hasBorder paddingSize="m">
              <EuiStat
                title={
                  payload?.generatedAt
                    ? new Date(payload.generatedAt).toLocaleTimeString()
                    : "-"
                }
                description={
                  payload?.cached
                    ? translate(`${I18N}.stat.generated.cached`, {
                        defaultMessage: "Stand (Cache)",
                      })
                    : translate(`${I18N}.stat.generated`, {
                        defaultMessage: "Stand",
                      })
                }
                titleSize="m"
              />
            </EuiPanel>
          </EuiFlexItem>
        </EuiFlexGroup>

        {/*
          State the exclusion rather than quietly shrinking the table: a reader
          counting rows against GitLab would otherwise wonder where the
          deployment-plumbing subgroups went.
        */}
        {(payload?.ignoredPaths?.length ?? 0) > 0 && (
          <>
            <EuiSpacer size="s" />
            <EuiText size="xs" color="subdued">
              <EuiIcon type="eyeClosed" size="s" />{" "}
              {translate(`${I18N}.ignored`, {
                defaultMessage:
                  "Nicht enthalten ({{count}} Projekte): {{paths}} - Deployment-Plumbing, keine versionierten Komponenten.",
                values: {
                  count: payload?.ignored ?? 0,
                  paths: (payload?.ignoredPaths ?? []).join(", "),
                },
              })}
            </EuiText>
          </>
        )}

        {/*
          These are wave modules the update manager still carries a version for,
          so their absence needs naming - otherwise the page silently disagrees
          with /opsanio/update about how many components exist.
        */}
        {(payload?.excludedArchived?.length ?? 0) > 0 && (
          <>
            <EuiSpacer size="s" />
            <EuiText size="xs" color="subdued">
              <EuiIcon type="folderCheck" size="s" />{" "}
              {translate(`${I18N}.excludedArchived`, {
                defaultMessage:
                  "In GitLab archiviert und daher nicht enthalten: {{paths}}. Der Wave-Update-Manager führt diese Komponenten weiterhin.",
                values: {
                  paths: (payload?.excludedArchived ?? []).join(", "),
                },
              })}
            </EuiText>
          </>
        )}

        {unreadable.length > 0 && (
          <>
            <EuiSpacer />
            <EuiCallOut
              color="warning"
              iconType="alert"
              title={translate(`${I18N}.unreadable.title`, {
                defaultMessage:
                  "{{count}} Projekt(e) konnten nicht aus GitLab gelesen werden",
                values: { count: unreadable.length },
              })}
            >
              <p>{unreadable.map((r) => r.projectPath).join(", ")}</p>
            </EuiCallOut>
          </>
        )}

        <EuiSpacer />

        <EuiPanel hasBorder paddingSize="m">
          <EuiInMemoryTable<VersionRow>
            items={rows}
            columns={columns}
            loading={loading}
            search={{
              box: {
                incremental: true,
                schema: false,
                placeholder: translate(`${I18N}.search.placeholder`, {
                  defaultMessage: "Projekt suchen...",
                }),
              },
              filters: [
                {
                  type: "field_value_selection",
                  field: "group",
                  name: translate(`${I18N}.filter.group`, {
                    defaultMessage: "Gruppe",
                  }),
                  multiSelect: "or",
                  options: groups.map((g) => ({ value: g, name: g })),
                },
                {
                  type: "field_value_selection",
                  field: "releaseHealth",
                  name: translate(`${I18N}.filter.health`, {
                    defaultMessage: "Release-Pipeline",
                  }),
                  multiSelect: "or",
                  options: [
                    {
                      value: "failing",
                      name: translate(`${I18N}.filter.health.failing`, {
                        defaultMessage: "fehlgeschlagen",
                      }),
                    },
                    {
                      value: "ok",
                      name: translate(`${I18N}.filter.health.ok`, {
                        defaultMessage: "erfolgreich",
                      }),
                    },
                    {
                      value: "none",
                      name: translate(`${I18N}.filter.health.none`, {
                        defaultMessage: "kein Release-Kanal",
                      }),
                    },
                  ],
                },
                {
                  type: "field_value_selection",
                  field: "tracked",
                  name: translate(`${I18N}.filter.tracked`, {
                    defaultMessage: "Art",
                  }),
                  multiSelect: "or",
                  options: [
                    {
                      value: translate(`${I18N}.tracked.yes`, {
                        defaultMessage: "Wave-Modul",
                      }),
                      name: translate(`${I18N}.tracked.yes`, {
                        defaultMessage: "Wave-Modul",
                      }),
                    },
                    {
                      value: translate(`${I18N}.tracked.no`, {
                        defaultMessage: "Sonstiges",
                      }),
                      name: translate(`${I18N}.tracked.no`, {
                        defaultMessage: "Sonstiges",
                      }),
                    },
                  ],
                },
              ],
            }}
            // Most-broken first: the point of the page is spotting what needs
            // attention, and 148 of ~167 projects are green. Ties keep the
            // alphabetical order the `rows` memo established.
            sorting={{ sort: { field: "failedCount", direction: "desc" } }}
            // `initialPageSize`, not `pageIndex`/`pageSize`: the latter puts
            // EuiInMemoryTable into controlled mode, where without an
            // `onTableChange` handler it pins itself back to page 0.
            pagination={{
              initialPageSize: 25,
              pageSizeOptions: [25, 50, 100, 200],
            }}
            message={
              <EuiEmptyPrompt
                iconType="tag"
                title={
                  <h3>
                    {translate(`${I18N}.empty.title`, {
                      defaultMessage: "Keine Versionen gefunden",
                    })}
                  </h3>
                }
                body={
                  <p>
                    {translate(`${I18N}.empty.body`, {
                      defaultMessage:
                        "Kein Projekt der Gruppe opsanio hatte in den letzten {{days}} Tagen Aktivität. Ein grösseres Zeitfenster liefert mehr Projekte (?days=90); ansonsten Token und Gruppen-ID prüfen.",
                      values: {
                        days: payload?.windowDays ?? FALLBACK_WINDOW_DAYS,
                      },
                    })}
                  </p>
                }
              />
            }
          />
        </EuiPanel>
      </EuiPageBody>
    </EuiPage>
  );
};

export default OpsanioVersionOverview;
